import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { FeatureFlag } from '@7chairs/types';
import { CircularProgress } from '@mui/material';
import { useHandleSearchParamsPlan } from '../../../hooks/useHandleSearchParamsPlan.hook';
import { useMst } from '../../../models/rootModel';
import { growthBook } from '../../../providers/featureFlag/growthbook';
import { PaywallMetaVariants } from '../../../types/paywall';
import { useAnalytics } from '../../../utils/analytics';
import { LoaderDiv } from '../../styledComponent/Boxes.style';
import { PickAProductContentV2 } from '../PickAProductContentV2/PickAProductContentV2';
import { PickAProductContentV3 } from '../PickAProductContentV2/PickAProductContentV3';
import {
  DEFAULT_PLAN_KEY,
  PickAProductProps,
  PRICE_TEST_DEFAULT_PLAN_KEY,
  Variant,
  WEEKLY_PRICES_TEST_DEFAULT_PLAN_KEY,
} from './PickAProduct.types';

export const PickAProduct = observer((props: PickAProductProps) => {
  const { UserPayment, UserProgress, UserDiscoveryResponse } = useMst();
  const { isActive, nextStep, stepName } = props;
  const analytics = useAnalytics();

  const [initialLoading, setInitialLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [variant, setVariant] = useState<Variant>(Variant.v2);

  useEffect(() => {
    const init = async () => {
      if (isActive) {
        const gbValue = growthBook?.getFeatureValue(FeatureFlag.useWebPricingTestWeekly, false);
        const variantToBeUsed: Variant = gbValue ? Variant.v3 : Variant.v2;
        setVariant(variantToBeUsed);

        if (variantToBeUsed === Variant.v3) {
          await UserPayment.getPaywallMetadata(PaywallMetaVariants.WeeklySessions);
        }

        analytics.track('view pick a product step', {
          stepName,
          referringUrl: document.referrer,
          segment: UserDiscoveryResponse.segment,
          variant: variantToBeUsed,
          healingProgram: false,
        });
        window.scrollTo(0, 0);
        setIsInitialized(true);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, analytics, stepName, UserDiscoveryResponse.segment, UserPayment.getPaywallMetadata]);

  useEffect(() => {
    if (isActive && !UserPayment.checkIfPlansAreLoaded()) {
      UserPayment.getStripePlans();
    }
  }, [UserPayment, isActive]);

  useHandleSearchParamsPlan({
    isActive,
    nextStep,
    setInitialLoading,
  });

  const pickAProduct = (pp: string, id: string) => {
    analytics.track('click product to buy', {
      sku: id,
      stepName,
      segment: UserDiscoveryResponse.segment,
      healingProgram: false,
    });
    UserPayment.setChosenPlanPosition(pp);
  };

  const plan = UserPayment.getChoosenPlan();

  const handleSubmit = (position: string) => {
    if (!plan) {
      UserPayment.setChosenPlanPosition(
        variant === Variant.v1
          ? DEFAULT_PLAN_KEY
          : variant === Variant.v2
          ? PRICE_TEST_DEFAULT_PLAN_KEY
          : WEEKLY_PRICES_TEST_DEFAULT_PLAN_KEY
      );
    }

    analytics.track('submit product to buy', {
      sku: UserPayment?.chosenPlanPosition && UserPayment.stripePlans[UserPayment.chosenPlanPosition].id,
      stepName,
      segment: UserDiscoveryResponse.segment,
      position,
      healingProgram: false,
    });

    nextStep();
  };

  const handleAbsoluteBack = () => {
    UserProgress.back(props.goToNamedStep);
  };

  if (initialLoading || !isInitialized) {
    return null;
  }

  return (
    <>
      {UserPayment.getPlanPositions(variant).length && UserPayment.payWallMeta ? (
        <>
          {variant !== Variant.v3 && (
            <PickAProductContentV2
              {...props}
              handleAbsoluteBack={handleAbsoluteBack}
              handleSubmit={handleSubmit}
              pickAProduct={pickAProduct}
              planPositions={UserPayment.getPlanPositions(variant)}
              data={UserPayment.payWallMeta}
              variant={variant}
            />
          )}
          {variant === Variant.v3 && (
            <PickAProductContentV3
              {...props}
              handleAbsoluteBack={handleAbsoluteBack}
              handleSubmit={handleSubmit}
              pickAProduct={pickAProduct}
              planPositions={UserPayment.getPlanPositions(variant)}
              data={UserPayment.payWallMeta}
            />
          )}
        </>
      ) : (
        <LoaderDiv>
          <CircularProgress />
        </LoaderDiv>
      )}
    </>
  );
});
